import * as utils from 'common/utils';
import { mapFeatureType } from 'common/charts/constants';
import {
  OVERLAY_TRACE_KEYS,
  pointHasTraceData
} from 'utils/overlayTraceUtils';

/**
 * Utility functions for spectrum overlay operations
 * Centralizes business logic for overlay functionality
 */

/**
 * Gets shaft speed from various sources with enhanced logic for different scenarios
 */
export const getShaftSpeed = (sources = {}) => {
  const { response, config, state } = sources;

  // Enhanced logic for selectedPoints (from ChartsView) - highest priority
  if (state && state.selectedPoints && state.selectedPoints.length > 0) {
    // First try to find a valid non-zero speed in any point
    const pointWithSpeed = state.selectedPoints.find((point) => {
      const speed = point.shaft_speed || point.speed;
      return speed && typeof speed === 'number' && speed > 0.001;
    });

    if (pointWithSpeed) {
      return pointWithSpeed.shaft_speed || pointWithSpeed.speed;
    }

    // If we didn't find a valid speed, check if any point has zero speed
    const pointWithZeroSpeed = state.selectedPoints.find((point) => {
      const speed = point.shaft_speed || point.speed;
      return speed === 0 || speed === 0.0 ||
        (typeof speed === 'number' && speed < 0.001) ||
        (typeof speed === 'string' && (speed.toLowerCase() === 'n/a' || speed === '0' || speed === '0.0'));
    });

    if (pointWithZeroSpeed) {
      return 0;
    }
  }

  // Check current spectrum data (from response or config)
  if (response && typeof response.shaft_speed === 'number') {
    if (response.shaft_speed > 0.001) {
      return response.shaft_speed;
    }
    if (response.shaft_speed === 0 || response.shaft_speed < 0.001) {
      return 0;
    }
  }

  if (config && config.spectrum) {
    const spectrumSpeed = config.spectrum.shaft_speed;
    if (spectrumSpeed && typeof spectrumSpeed === 'number' && spectrumSpeed > 0.001) {
      return spectrumSpeed;
    }
    if (spectrumSpeed === 0 || spectrumSpeed === 0.0 ||
      (typeof spectrumSpeed === 'number' && spectrumSpeed < 0.001)) {
      return 0;
    }
  }

  // Check current selection (waveAndSpecSelection)
  if (state && state.waveAndSpecSelection) {
    const selectionSpeed = state.waveAndSpecSelection.speed || state.waveAndSpecSelection.shaft_speed;
    if (selectionSpeed && typeof selectionSpeed === 'number' && selectionSpeed > 0.001) {
      return selectionSpeed;
    }
    if (selectionSpeed === 0 || selectionSpeed === 0.0 ||
      (typeof selectionSpeed === 'number' && selectionSpeed < 0.001) ||
      (typeof selectionSpeed === 'string' && (selectionSpeed.toLowerCase() === 'n/a' || selectionSpeed === '0'))) {
      return 0;
    }
  }

  // Legacy support for simple state.shaftSpeed
  if (state && state.shaftSpeed) {
    return state.shaftSpeed;
  }

  // Default fallback - assuming we're likely in Hz mode
  return null;
};

/**
 * Handles Y-axis unit conversion for spectrum data with enhanced validation
 */
export const convertSpectrumYAxis = (spectrumData, sourceUnit, targetUnit) => {
  if (!spectrumData || !sourceUnit || !targetUnit || sourceUnit === targetUnit) {
    return spectrumData;
  }

  // ENHANCED: Validate input data
  if (!Array.isArray(spectrumData) || spectrumData.length === 0) {
    return spectrumData;
  }

  const reference = utils.getAmplitudeConversionReference(sourceUnit, targetUnit);
  if (!reference) return spectrumData;

  return spectrumData.map((point) => {
    // ENHANCED: Validate point data
    if (!point || typeof point.y !== 'number' || !Number.isFinite(point.y)) {
      return point;
    }

    let convertedY = point.y;

    if (targetUnit.toLowerCase() === 'db' && sourceUnit.toLowerCase() !== 'db') {
      convertedY = utils.convertToDbScale(point.y, reference);
    } else if (sourceUnit.toLowerCase() === 'db' && targetUnit.toLowerCase() !== 'db') {
      convertedY = utils.convertFromDbScale(point.y, reference);
    }

    // ENHANCED: Validate converted value
    if (!Number.isFinite(convertedY)) {
      console.warn(`Invalid Y-axis conversion result: ${point.y} -> ${convertedY}`);
      return point; // Return original if conversion failed
    }

    return {
      ...point,
      y: convertedY
    };
  });
};

/**
 * Filters selected points for display based on the current chart mode (narrowband vs. broadband)
 * Uses dynamic feature classification from backend constants instead of hard-coded arrays.
 * For narrowband analysis, points need both valid speed and spectrum data.
 * For broadband analysis, points need valid spectrum data (speed can be zero).
 * Points that don't meet the requirements are filtered out rather than causing errors.
 *
 * @param {Array} selectedPoints - Array of selected overlay points
 * @param {string} feature - Current feature (determines if narrowband filtering applies)
 * @param {boolean} showOverlay - Whether overlay mode is active
 * @returns {Array} - Filtered points suitable for display and analysis
 */
export const getFilteredPointsForDisplay = (selectedPoints, feature, showOverlay) =>
  getFilteredPointsForDisplayByTrace(
    selectedPoints,
    feature,
    showOverlay,
    OVERLAY_TRACE_KEYS.SPECTRUM
  );

/**
 * Selected overlay points for legend display, including entries still loading trace data.
 * Does not require spectrum_data / waveform_data to be present yet.
 */
export const getOverlayLegendPoints = (selectedPoints, feature, showOverlay, dataKey) => {
  if (!showOverlay || !selectedPoints) {
    return selectedPoints || [];
  }

  if (dataKey === OVERLAY_TRACE_KEYS.WAVEFORM) {
    return selectedPoints;
  }

  if (!feature || typeof feature !== 'string') {
    return selectedPoints;
  }

  const isBroadband = mapFeatureType[feature] === 'Broadband';
  if (isBroadband) {
    return selectedPoints;
  }

  return selectedPoints.filter((p) => {
    const speed = p.shaft_speed || p.speed;
    return typeof speed === 'number' && speed > 0.001 && Number.isFinite(speed);
  });
};

export const getFilteredPointsForDisplayByTrace = (
  selectedPoints,
  feature,
  showOverlay,
  dataKey
) => {
  if (!showOverlay || !selectedPoints) {
    return selectedPoints || [];
  }

  if (dataKey === OVERLAY_TRACE_KEYS.WAVEFORM) {
    return selectedPoints.filter(p => pointHasTraceData(p, OVERLAY_TRACE_KEYS.WAVEFORM));
  }

  // ENHANCED: Validate feature parameter
  if (!feature || typeof feature !== 'string') {
    return selectedPoints.filter(p => pointHasTraceData(p, OVERLAY_TRACE_KEYS.SPECTRUM));
  }

  const isBroadband = mapFeatureType[feature] === 'Broadband';
  const isNarrowband = !isBroadband;

  if (isNarrowband) {
    return selectedPoints.filter((p) => {
      const speed = p.shaft_speed || p.speed;
      const hasValidSpeed = typeof speed === 'number' && speed > 0.001 && Number.isFinite(speed);
      const hasValidSpectrumData = pointHasTraceData(p, OVERLAY_TRACE_KEYS.SPECTRUM);
      return hasValidSpeed && hasValidSpectrumData;
    });
  }

  return selectedPoints.filter(p => pointHasTraceData(p, OVERLAY_TRACE_KEYS.SPECTRUM));
};
